// btw-front/src/api/client.js
// fetch 래퍼 — Authorization Bearer 자동 부착, 401 처리, JSON/멀티파트 분기.

const BASE = "/api"; // CRA proxy → localhost:5999 (dev) / nginx (prod)
const TOKEN_KEY = "btw.partner.accessToken";

export function getAccessToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || "";
  } catch (e) {
    return "";
  }
}

export function setAccessToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch (e) {
    /* ignore */
  }
}

// 401 발생 시 호출되는 핸들러 — App 부트스트랩 시점에 setOnUnauthenticated(...)
// 으로 PartnerSessionContext.logout() 연결.
let onUnauthenticated = null;
export function setOnUnauthenticated(fn) {
  onUnauthenticated = typeof fn === "function" ? fn : null;
}

async function parseBody(res) {
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) return res.json();
  return res.text();
}

export class ApiError extends Error {
  constructor(status, payload) {
    // 다양한 형식 수용:
    //   ① 우리 표준: { error: { code, message } }
    //   ② Spring/타 서비스: { code, message }
    //   ③ 평문 string
    //   ④ 그 외 — fallback
    let msg = null;
    if (payload && typeof payload === "object") {
      msg = (payload.error && payload.error.message) || payload.message || null;
    } else if (typeof payload === "string" && payload.trim()) {
      msg = payload.trim();
    }
    super(msg ? `${msg} (HTTP ${status})` : `HTTP ${status}`);
    this.status = status;
    this.payload = payload;
    this.code =
      (payload && payload.error && payload.error.code) || (payload && payload.code) || null;
  }
}

async function request(method, path, { body, multipart, headers } = {}) {
  const finalHeaders = { ...(headers || {}) };
  const token = getAccessToken();
  if (token) finalHeaders.Authorization = `Bearer ${token}`;

  let payload;
  if (multipart) {
    // body 가 FormData — Content-Type 은 브라우저가 자동 (boundary 포함)
    payload = body;
  } else if (body !== undefined) {
    finalHeaders["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  const res = await fetch(BASE + path, { method, headers: finalHeaders, body: payload });

  // 401 — 토큰 만료/위변조 → 자동 로그아웃
  if (res.status === 401) {
    setAccessToken(null);
    if (onUnauthenticated) onUnauthenticated();
    const parsed = await parseBody(res).catch(() => null);
    throw new ApiError(401, parsed);
  }

  if (!res.ok) {
    const parsed = await parseBody(res).catch(() => null);
    throw new ApiError(res.status, parsed);
  }

  if (res.status === 204) return null;
  return parseBody(res);
}

export const api = {
  get: (path, opts) => request("GET", path, opts),
  post: (path, body, opts) => request("POST", path, { ...(opts || {}), body }),
  postForm: (path, formData, opts) =>
    request("POST", path, { ...(opts || {}), body: formData, multipart: true }),
  put: (path, body, opts) => request("PUT", path, { ...(opts || {}), body }),
  del: (path, opts) => request("DELETE", path, opts),
};
